import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Text } from 'grommet';
import {
  FormNext,
  Download,
  StatusGood,
  StatusCritical,
} from 'grommet-icons';
import Papa from 'papaparse';
import { AppRadioButton, Spinning } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductFormMultipleTextInputs } from '@Components/Partial/Product/NewSearch';
import { isValidYouTubeChannelUrl } from '@Helpers/util';
import {
  StyledUploadContainer as BaseStyledUploadContainer,
  StyledUploadInput,
} from '@Components/Partial/Product/NewSearch/StyledProductFormComponent.jsx';
import {
  StyledAccountTypeOption,
  StyledPlanOption,
  StyledUppercaseText,
  StyledStrikethroughText,
  StyledResponsiveContainer,
  StyledFlexContainer,
  StyledFlexText,
  StyledNowrapButton,
} from '../StyledOnboardingComponents';

const StyledUploadContainer = styled(BaseStyledUploadContainer)`
  width: 100%;
`;

const formatCountdown = (seconds) => {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m} minutes`;
  return 'less than a minute';
};

// Default/fallback uploader option features (without early bird)
const DEFAULT_UPLOADER_FEATURES = {
  redflag: [
    'We guide formatting and preparation',
    'Managed import into Content ID',
    'Setup fee (per channel): $200',
  ],
  self: [
    'Upload directly via your dashboard',
    'Full control over asset management',
    'Setup fee (per channel): $100',
  ],
};

// Default/fallback early bird uploader option features
const DEFAULT_UPLOADER_FEATURES_EARLY_BIRD = {
  redflag: [
    'We guide formatting and preparation',
    'Managed import into Content ID',
    { text: 'Setup fee (per channel): ', strikethrough: '$200', regular: ' $100 (Early Bird)' },
  ],
  self: [
    'Upload directly via your dashboard',
    'Full control over asset management',
    { text: 'Setup fee (per channel): ', strikethrough: '$100', regular: ' $50 (Early Bird)' },
  ],
};

const DEFAULT_PLAN_OPTIONS = [
  {
    key: 'A',
    label: 'Revenue Share',
    tag: '$0/month · 25%',
    features: [
      'Monthly fee: $0',
      '25% of recovered revenue',
      'Best for growing channels',
    ],
  },
  {
    key: 'B',
    label: 'Hybrid',
    tag: '$1000/month · Reduced share',
    features: [
      'Monthly fee: $1000',
      '0% for 3 months, then 10%',
      'Best for high-volume channels',
    ],
  },
];

// Constants for plan type mapping
const PLAN_TYPE_MAP = { A: 'revenueShare', B: 'hybrid' };

// Fallback values
const FALLBACK_SETUP_FEES = {
  redflag: { revenueShare: 200, hybrid: 200 },
  self: { revenueShare: 100, hybrid: 100 },
};

const CidConfigurationStep = ({
  small,
  offerings,
  channels,
  setChannels,
  uploader,
  setUploader,
  cidPlan,
  setCidPlan,
  channelLimit,
  accountKind,
  setCsvChannels,
  csvChannels: parentCsvChannels,
  csvFileName: parentCsvFileName,
  setCsvFileName,
  pricingConfig,
  earlyBirdEligibility,
  youtubeEligibility,
  onRetryEligibility,
  allowlistChannels = [],
  setAllowlistChannels = null,
}) => {
  const { theme: { page, button } } = useAppTheme();

  const [csvError, setCsvError] = React.useState('');
  const [channelError, setChannelError] = React.useState('');
  const csvChannels = parentCsvChannels || [];
  const csvFileName = parentCsvFileName || '';
  const eligibilityStatus = youtubeEligibility?.status || 'idle';
  const eligibilityData = youtubeEligibility?.data || null;
  const eligibilityError = youtubeEligibility?.error || null;

  // Helper function to format currency
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  const formatSubscriberCount = (count) => {
    if (typeof count !== 'number') {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-US').format(count);
  };

  // Helper to get config value for a specific uploader/plan combination
  const getConfigValue = React.useCallback((uploaderKey, planKey) => {
    const planType = PLAN_TYPE_MAP[planKey];
    return pricingConfig?.rules?.youtubeCid?.[uploaderKey]?.[planType];
  }, [pricingConfig]);

  // Extract pricing values from config with fallbacks
  const getSetupFee = React.useCallback((uploaderKey, planKey) => {
    const planType = PLAN_TYPE_MAP[planKey];
    const configValue = getConfigValue(uploaderKey, planKey);
    return configValue?.setupFeeAmount
      ?? FALLBACK_SETUP_FEES[uploaderKey]?.[planType]
      ?? 100;
  }, [getConfigValue]);

  const getEarlyBirdSetupFee = React.useCallback((uploaderKey, planKey) => {
    const configValue = getConfigValue(uploaderKey, planKey);
    return configValue?.earlyBirdSetupFeeAmount ?? null;
  }, [getConfigValue]);

  const getMonthlyFee = React.useCallback((uploaderKey, planKey) => {
    const configValue = getConfigValue(uploaderKey, planKey);
    const fallback = planKey === 'A' ? 0 : 1000;
    return configValue?.monthlyFeeAmount ?? fallback;
  }, [getConfigValue]);

  const getRevenueSharePercent = React.useCallback((uploaderKey, planKey) => {
    if (planKey !== 'A') return null; // Hybrid handled separately
    const configValue = getConfigValue(uploaderKey, planKey);
    return configValue?.revenueSharePercent ?? 25;
  }, [getConfigValue]);

  const getHybridDetails = React.useCallback((uploaderKey) => {
    const configValue = pricingConfig?.rules?.youtubeCid?.[uploaderKey]?.hybrid;
    return {
      initialMonths: configValue?.initialMonths ?? 3,
      initialSharePercent: configValue?.initialSharePercent ?? 0,
      afterSharePercent: configValue?.afterSharePercent ?? 10,
    };
  }, [pricingConfig]);

  const getPlanLabel = React.useCallback((planKey) => {
    const planType = PLAN_TYPE_MAP[planKey];
    const preLabelIdentifier = uploader === 'redflag' ? 'Plan A' : 'Plan B';
    const useLabel = pricingConfig?.display?.planLabels?.[planType]
      ?? (planKey === 'A' ? 'Revenue Share' : 'Hybrid');

    return `${preLabelIdentifier}: ${useLabel}`;
  }, [pricingConfig, uploader]);

  const isEarlyBirdEligible = Boolean(earlyBirdEligibility?.eligible);
  const makeSetupFeeFeature = (fullFee, earlyBirdFee) => {
    if (isEarlyBirdEligible && earlyBirdFee != null) {
      return {
        text: 'Setup fee (per channel): ',
        strikethrough: formatCurrency(fullFee),
        regular: ` ${formatCurrency(earlyBirdFee)} (Early Bird)`,
      };
    }
    return `Setup fee (per channel): ${formatCurrency(fullFee)}`;
  };

  // Build dynamic options from config or use defaults
  const UPLOADER_OPTIONS = React.useMemo(() => {
    if (!pricingConfig) {
      // Use pre-built default features, choosing early bird variant when eligible
      return [
        {
          key: 'redflag',
          label: 'Plan A: Redflag Managed',
          tag: 'White-glove onboarding',
          features: isEarlyBirdEligible
            ? DEFAULT_UPLOADER_FEATURES_EARLY_BIRD.redflag
            : DEFAULT_UPLOADER_FEATURES.redflag,
        },
        {
          key: 'self',
          label: 'Plan B: Self-Managed',
          tag: 'Lower setup cost',
          features: isEarlyBirdEligible
            ? DEFAULT_UPLOADER_FEATURES_EARLY_BIRD.self
            : DEFAULT_UPLOADER_FEATURES.self,
        },
      ];
    }

    const redflagSetupFee = getSetupFee('redflag', 'A');
    const selfSetupFee = getSetupFee('self', 'A');
    const redflagEarlyBirdFee = getEarlyBirdSetupFee('redflag', 'A');
    const selfEarlyBirdFee = getEarlyBirdSetupFee('self', 'A');

    return [
      {
        key: 'redflag',
        label: 'Plan A: Redflag Managed',
        tag: 'White-glove onboarding',
        features: [
          'We guide formatting and preparation',
          'Managed import into Content ID',
          makeSetupFeeFeature(redflagSetupFee, redflagEarlyBirdFee),
        ],
      },
      {
        key: 'self',
        label: 'Plan B: Self-Managed',
        tag: 'Lower setup cost',
        features: [
          'Upload directly via your dashboard',
          'Full control over asset management',
          makeSetupFeeFeature(selfSetupFee, selfEarlyBirdFee),
        ],
      },
    ];
  }, [pricingConfig, getSetupFee, getEarlyBirdSetupFee, isEarlyBirdEligible]);

  const PLAN_OPTIONS = React.useMemo(() => {
    if (!pricingConfig) return DEFAULT_PLAN_OPTIONS;

    const revenueShareMonthly = getMonthlyFee(uploader, 'A');
    const hybridMonthly = getMonthlyFee(uploader, 'B');
    const revenueSharePercent = getRevenueSharePercent(uploader, 'A');
    const hybridDetails = getHybridDetails(uploader);

    return [
      {
        key: 'A',
        label: getPlanLabel('A'),
        tag: `${formatCurrency(revenueShareMonthly)}/month · ${revenueSharePercent}%`,
        features: [
          `Monthly fee: ${formatCurrency(revenueShareMonthly)}`,
          `${revenueSharePercent}% of recovered revenue`,
          'Best for growing channels',
        ],
      },
      {
        key: 'B',
        label: getPlanLabel('B'),
        tag: `${formatCurrency(hybridMonthly)}/month · Reduced share`,
        features: [
          `Monthly fee: ${formatCurrency(hybridMonthly)}`,
          `${hybridDetails.initialSharePercent}% for ${hybridDetails.initialMonths} months, then ${hybridDetails.afterSharePercent}%`,
          'Best for high-volume channels',
        ],
      },
    ];
  }, [
    pricingConfig,
    uploader,
    getMonthlyFee,
    getRevenueSharePercent,
    getHybridDetails,
    getPlanLabel,
  ]);

  const isAgency = accountKind === 'agency';
  const manualChannels = channels.filter((c) => c.trim() !== '');
  const allChannels = React.useMemo(
    () => [...manualChannels, ...csvChannels],
    [manualChannels, csvChannels],
  );

  // Reset CSV state when account kind changes
  React.useEffect(() => {
    if (!isAgency) {
      setCsvError('');
      setCsvChannels([]);
      setCsvFileName('');
    }
  }, [isAgency, setCsvChannels, setCsvFileName]);

  const validateChannel = (channel) => {
    if (!channel || !channel.trim()) return false;
    return isValidYouTubeChannelUrl(channel.trim());
  };

  // Helper to check for duplicates across manual and CSV channels
  const hasDuplicateChannels = (manual, csv) => {
    const all = [...manual, ...csv].map((ch) => ch.trim().toLowerCase()).filter(Boolean);
    const unique = new Set(all);
    return unique.size !== all.length;
  };

  const handleCsvChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setCsvError('');
      setCsvChannels([]);
      setCsvFileName('');
      return;
    }

    setCsvFileName(file.name);
    setCsvError('');

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (!results.data || results.data.length === 0) {
            setCsvError('CSV file must contain at least one row of data.');
            return;
          }

          // Extract channels from CSV - expect a "channels" column
          const extractedChannels = results.data
            .map((row) => {
              // Try different possible column names (case-insensitive)
              const channelValue = row.channels || row.Channels || row.CHANNELS
                || row.channel || row.Channel || row.CHANNEL;
              return channelValue ? String(channelValue).trim() : null;
            })
            .filter((ch) => ch && ch.length > 0);

          if (extractedChannels.length === 0) {
            setCsvError(
              'No valid channels found in CSV. Please ensure the CSV has a "channels" column.',
            );
            return;
          }

          // Validate all CSV channels
          const invalidChannels = extractedChannels.filter(
            (ch) => !validateChannel(ch),
          );
          if (invalidChannels.length > 0) {
            setCsvError(
              `Invalid channel format found in CSV: ${invalidChannels[0]}. Please ensure all channels are valid YouTube URLs.`,
            );
            return;
          }

          // Check if total channels (manual + CSV) exceed the limit
          if (manualChannels.length + extractedChannels.length > channelLimit) {
            const allowed = channelLimit - manualChannels.length;
            setCsvError(
              `CSV contains ${extractedChannels.length} channels. Only ${allowed} more channel(s) allowed.`,
            );
            return;
          }

          // Store CSV channels separately (don't add to input fields)
          setCsvChannels(extractedChannels);
          setCsvError('');
        } catch (err) {
          setCsvError(err.message || 'Error parsing CSV file.');
        }
      },
      error: () => {
        setCsvError('Invalid CSV format.');
      },
    });
  };

  const handleDownloadSampleCsv = () => {
    const csvContent = 'channels\nhttps://www.youtube.com/@example';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_channels.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!offerings.useCid) {
    return (
      <Box gap="1rem">
        <Box gap="0.25rem">
          <Text weight={600} size="1.25rem" color={page.primaryText}>YouTube Content ID</Text>
          <Text size="0.85rem" color={page.secondaryText}>
            Content ID is not selected. Enable it in Services first to configure this step.
          </Text>
        </Box>
        <Box
          round="var(--app-radius)"
          pad="1rem"
          background={page.cardBg}
          gap="0.5rem"
        >
          <Text size="1rem" color={page.secondaryText}>
            You can turn it on in the
            {' '}
            <Text weight={700} color={page.primaryText}>Services</Text>
            {' '}
            step.
          </Text>
        </Box>
      </Box>
    );
  }

  const renderUploaderOptions = () => UPLOADER_OPTIONS.map((option) => {
    const active = uploader === option.key;
    return (
      <StyledResponsiveContainer
        key={option.key}
        gap="0.5rem"
        flex="grow"
        basis={small ? '100%' : '48%'}
      >
        <StyledAccountTypeOption
          active={active}
          highlightColor={button.primaryBg}
          hoverBg={button.selectBg}
          tabIndex={0}
          onClick={() => setUploader(option.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setUploader(option.key);
          }}
          margin={{ bottom: small ? 'small' : undefined }}
          flex="grow"
          round="var(--app-radius)"
          pad="0.65rem"
          width="100%"
        >
          <Box direction={small ? 'column' : 'row'} align="start" gap="0.75rem">
            <Box pad={{ top: small ? '0' : '0.15rem' }} flex={false}>
              <AppRadioButton
                name="uploader-type"
                checked={active}
                onChange={() => setUploader(option.key)}
                color={page.primaryText}
                highlightColor={button.primaryBg}
                borderColor={active ? button.primaryBg : button.border}
                label={null}
              />
            </Box>
            <Box gap="0.65rem" flex="grow" width="100%">
              <Box gap="0.25rem">
                <Text size="1.1rem" weight={700} color={page.primaryText}>
                  {option.label}
                </Text>
                <Text size="0.85rem" color={page.secondaryText}>
                  {option.tag}
                </Text>
              </Box>
              <Box gap="0.5rem">
                {option.features.map((feature) => {
                  const featureKey = typeof feature === 'string'
                    ? `feature-${option.key}-${feature}`
                    : `feature-${option.key}-${feature.text}-${feature.strikethrough}`;
                  return (
                    <Box key={featureKey} direction="row" gap="0.5rem" align="center">
                      <FormNext size="1rem" color="var(--page-alt-highlight)" />
                      <Text size="0.85rem" color={page.primaryText} weight={500}>
                        {typeof feature === 'string' ? feature : (
                          <>
                            {feature.text}
                            <StyledStrikethroughText as="span">
                              {feature.strikethrough}
                            </StyledStrikethroughText>
                            {feature.regular}
                          </>
                        )}
                      </Text>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </StyledAccountTypeOption>
        {active && (
          <StyledFlexContainer
            direction={small ? 'column' : 'row'}
            gap="1rem"
            margin={{ top: '0.5rem' }}
            width="100%"
            wrap
          >
            {PLAN_OPTIONS.map((planOption) => {
              const planActive = cidPlan === planOption.key;
              return (
                <StyledResponsiveContainer
                  key={planOption.key}
                  basis={small ? '100%' : '48%'}
                >
                  <StyledPlanOption
                    active={planActive}
                    highlightColor={button.primaryBg}
                    hoverBg={button.selectBg}
                    tabIndex={0}
                    onClick={() => setCidPlan(planOption.key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setCidPlan(planOption.key);
                    }}
                    round="var(--app-radius)"
                    pad="0.65rem"
                    gap="0.65rem"
                  >
                    <Box gap="0.25rem">
                      <Text size="1rem" weight={700} color={page.primaryText}>
                        {planOption.label}
                      </Text>
                      <Text size="0.85rem" color={page.secondaryText}>
                        {planOption.tag}
                      </Text>
                    </Box>
                    <Box gap="0.5rem">
                      {planOption.features.map((feature) => (
                        <Box key={feature} direction="row" gap="0.5rem" align="center">
                          <FormNext size="1rem" color="var(--page-alt-highlight)" />
                          <Text size="0.85rem" color={page.primaryText} weight={500}>
                            {feature}
                          </Text>
                        </Box>
                      ))}
                    </Box>
                  </StyledPlanOption>
                </StyledResponsiveContainer>
              );
            })}
          </StyledFlexContainer>
        )}
      </StyledResponsiveContainer>
    );
  });


  return (
    <Box gap="1rem">
      <Box gap="0.25rem">
        <Text weight={600} size="1.25rem" color={page.primaryText}>YouTube Content ID</Text>
        <Text size="0.85rem" color={page.secondaryText}>
          Add your channels and choose a plan.
        </Text>
      </Box>
      <Box
        round="var(--app-radius)"
        pad="1rem"
        background={page.cardBg}
        gap="0.35rem"
      >
        <Box
          direction={small ? 'column' : 'row'}
          justify="between"
          align={small ? 'start' : 'center'}
          gap="0.5rem"
          margin={{ bottom: '0.25rem' }}
        >
          <Box gap="0.25rem">
            <Text size="1rem" color={page.primaryText} weight={600}>
              Channels
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              Add YouTube channels with at least 100,000 subscribers.
            </Text>
          </Box>
          <Box
            pad={{ vertical: '0.3rem', horizontal: '0.5rem' }}
            round="var(--app-radius)"
            background={button.selectBg}
          >
            <StyledUppercaseText uppercase size="1rem" weight={700} color={page.altHighlight}>
              Limit:
              {' '}
              {channelLimit}
            </StyledUppercaseText>
          </Box>
        </Box>
        <Box
          direction={small ? 'column' : 'row'}
          justify="between"
          align="center"
          gap="0.5rem"
          margin={{ bottom: '0.25rem' }}
        >
          <Box direction="row" gap="0.5rem" align="center" flex="grow">
            <Text size="0.85rem" color={page.secondaryText}>
              {allChannels.length}
              {' / '}
              {channelLimit}
              {' added'}
              {csvChannels.length > 0 && (
                <>
                  {' '}
                  <Text size="0.85rem" color={button.primaryBg} weight={600}>
                    (
                    {
                      csvChannels.length
                    }
                    {' from CSV'}
                    {manualChannels.length > 0 && `, ${manualChannels.length} manual`}
                    )
                  </Text>
                </>
              )}
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              ·
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              Paste a URL or handle.
            </Text>
          </Box>
        </Box>
        <Box width="100%">
          <ProductFormMultipleTextInputs
            maxInputs={channelLimit - csvChannels.length}
            value={channels.filter((c) => c.trim() !== '')}
            handleFormValues={(vals) => {
              setChannelError('');

              // Validate each channel
              const invalidChannels = vals.filter(
                (ch) => ch.trim() && !validateChannel(ch.trim()),
              );
              if (invalidChannels.length > 0) {
                setChannelError(
                  `Invalid channel format: ${invalidChannels[0]}. Please enter a valid YouTube channel URL (e.g., https://www.youtube.com/@channel).`,
                );
                return;
              }

              // Remove empty strings
              const cleaned = vals.filter((c) => c.trim() !== '');

              // Check for duplicates across manual and CSV channels
              if (hasDuplicateChannels(cleaned, csvChannels)) {
                setChannelError(
                  'Duplicate channel detected. Please ensure each channel is unique.',
                );
                return;
              }

              // Check total limit including CSV channels
              if (cleaned.length + csvChannels.length > channelLimit) {
                setChannelError(
                  `Channel limit exceeded. You can add ${channelLimit - csvChannels.length} more manual channel(s).`,
                );
                return;
              }

              // Add empty string at end only if we have channels and under limit
              if (cleaned.length === 0) {
                setChannels([]);
              } else if (cleaned.length < channelLimit - csvChannels.length) {
                setChannels([...cleaned, '']);
              } else {
                setChannels(cleaned);
              }
            }}
            focusHighlight={page.focusHighlight}
            borderColor={page.inputBorder}
            placeholder="https://www.youtube.com/@channel"
            buttonAlign="start"
            onPendingInputChange={() => {}}
          />
          {channelError && (
            <Box margin={{ top: '0.5rem' }}>
              <Text size="0.85rem" color="var(--app-error)">
                {channelError}
              </Text>
            </Box>
          )}
        </Box>
        {isAgency && (
          <Box
            margin={{ top: '0.5rem' }}
            round="0.35rem"
            pad="0.75rem"
            background={page.cardBg}
            border={{ color: page.statusSuccess, size: '1px' }}
            gap="0.35rem"
          >
            <Text size="0.85rem" color={page.statusSuccess} weight={700}>
              Agency Notice
            </Text>
            <Text size="0.85rem" color={page.primaryText}>
              Please ensure all channels have at least 100,000 subscribers.
              Channels that do not meet this requirement will not be eligible for Content ID.
            </Text>
          </Box>
        )}
        {!isAgency && eligibilityStatus === 'checking' && (
          <Box
            margin={{ top: '0.5rem' }}
            round="0.35rem"
            pad="0.75rem"
            background={page.cardBg}
            border={{ color: button.border, size: '1px' }}
            direction="row"
            gap="0.5rem"
            align="center"
          >
            <Spinning size="small" color={page.secondaryText} />
            <Text size="0.85rem" color={page.secondaryText} weight={600}>
              Checking YouTube subscriber eligibility...
            </Text>
          </Box>
        )}
        {!isAgency && eligibilityStatus !== 'idle' && eligibilityData?.channels?.length > 0 && (
          <Box
            margin={{ top: '0.5rem' }}
            round="0.35rem"
            pad="0.75rem"
            background={page.cardBg}
            border={{ color: button.border, size: '1px' }}
            gap="0.5rem"
          >
            <Text size="0.9rem" color={page.primaryText} weight={700}>
              Channel Eligibility
            </Text>
            {eligibilityData.channels.map((channel) => (
              <Box
                key={channel.inputUrl}
                round="0.25rem"
                pad={{ vertical: '0.5rem', left: '0.75rem', right: '0.5rem' }}
                background={page.altBg}
                border={{
                  side: 'left',
                  size: '3px',
                  color: channel.eligible ? page.statusSuccess : page.statusError,
                }}
                gap="0.2rem"
              >
                <Box direction="row" align="center" gap="0.4rem">
                  {channel.eligible
                    ? <StatusGood size="small" color={page.statusSuccess} />
                    : <StatusCritical size="small" color={page.statusError} />}
                  <Text size="0.9rem" color={page.primaryText} weight={700}>
                    {channel.resolvedTitle || channel.inputUrl}
                  </Text>
                </Box>
                {channel.resolvedTitle && (
                  <Text size="0.75rem" color={page.secondaryText}>
                    {channel.inputUrl}
                  </Text>
                )}
                <Box direction="row" gap="0.35rem" align="center">
                  <Text size="0.75rem" color={page.secondaryText}>
                    {formatSubscriberCount(channel.subscriberCount)}
                    {' '}
                    subscribers
                  </Text>
                  <Text size="0.75rem" color={page.secondaryText}>·</Text>
                  <Text
                    size="0.75rem"
                    weight={600}
                    color={channel.eligible ? page.statusSuccess : page.statusError}
                  >
                    {channel.reason}
                  </Text>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        {!isAgency && eligibilityStatus === 'eligible' && (
          <Box
            margin={{ top: '0.5rem' }}
            round="0.35rem"
            pad="0.75rem"
            background={page.cardBg}
            border={{ color: page.statusSuccess, size: '1px' }}
            direction="row"
            gap="0.5rem"
            align="center"
          >
            <Text size="0.85rem" color={page.statusSuccess} weight={700}>
              All channels eligible — you may proceed.
            </Text>
          </Box>
        )}
        {!isAgency && eligibilityStatus === 'ineligible' && (
          <Box margin={{ top: '0.5rem' }}>
            <Text size="0.85rem" color={page.statusError} weight={600}>
              {eligibilityData?.message || 'One or more channels are not eligible for onboarding.'}
            </Text>
          </Box>
        )}
        {!isAgency && eligibilityStatus === 'error' && (
          <Box
            margin={{ top: '0.5rem' }}
            direction="row"
            gap="0.75rem"
            align="center"
          >
            <Text size="0.85rem" color={page.statusError} weight={600}>
              {eligibilityError || 'Eligibility check failed. Please retry.'}
            </Text>
            {onRetryEligibility && (
              <StyledNowrapButton
                level="xsmall"
                label="Retry"
                width="fit-content"
                color={button.primaryBg}
                onClick={onRetryEligibility}
              />
            )}
          </Box>
        )}
        {isAgency && (
          <Box gap="0.5rem" margin={{ top: '0.5rem' }}>
            <Box direction="row" gap="0.5rem" align="center">
              <Text size="0.85rem" color={page.primaryText} weight={500}>
                Or upload CSV:
              </Text>
              <StyledNowrapButton
                level="xsmall"
                label="Download Sample CSV"
                width="fit-content"
                color={button.primaryBg}
                icon={<Download size="small" />}
                onClick={handleDownloadSampleCsv}
              />
            </Box>
            <StyledUploadContainer highlight={page.focusHighlight}>
              <Box fill gap="0.75rem" align="center" direction="row" pad="0.5rem">
                <StyledFlexText
                  size="0.875rem"
                  color={csvFileName ? button.primaryBg : page.primaryText}
                  weight={csvFileName ? 400 : 700}
                >
                  {csvFileName || 'No CSV selected'}
                </StyledFlexText>
                <StyledNowrapButton
                  level="xsmall"
                  label="Choose CSV..."
                  width="fit-content"
                  color={page.focusHighlight}
                  disabled={false}
                />
              </Box>
              <StyledUploadInput
                type="file"
                accept=".csv"
                onChange={handleCsvChange}
                highlight={page.focusHighlight}
              />
            </StyledUploadContainer>
            {csvError && (
              <Box>
                <Text size="0.85rem" color="var(--app-error)">
                  {csvError}
                </Text>
              </Box>
            )}
          </Box>
        )}
      </Box>
      {setAllowlistChannels && (
        <Box
          round="var(--app-radius)"
          pad="1rem"
          background={page.cardBg}
          gap="0.75rem"
        >
          <Box gap="0.25rem">
            <Text size="1rem" color={page.primaryText} weight={600}>
              Channel Allowlist
              {' '}
              <Text size="0.85rem" color={page.secondaryText} weight={400}>(Optional)</Text>
            </Text>
            <Text size="0.85rem" color={page.secondaryText}>
              Add any YouTube channels you want to protect from accidental claims — for example,
              your secondary channels, official collaborators, or licensed re-uploaders.
              Our system will never file a Content ID claim or DMCA takedown against these channels.
            </Text>
          </Box>
          <ProductFormMultipleTextInputs
            value={allowlistChannels}
            handleFormValues={(updated) => setAllowlistChannels(updated)}
            onPendingInputChange={() => {}}
            placeholder="https://www.youtube.com/@channelname"
            maxInputs={50}
            focusHighlight={page.focusHighlight}
          />
        </Box>
      )}
      {isEarlyBirdEligible && (
        <Box
          round="var(--app-radius)"
          pad={{ vertical: '0.65rem', horizontal: '1rem' }}
          background="#FFF3CD"
          direction="row"
          gap="0.5rem"
          align="center"
        >
          <Text size="0.9rem" weight={700} color="#856404">
            Early Bird Offer Active
          </Text>
          {earlyBirdEligibility.remainingSeconds > 0 && (
            <Text size="0.85rem" color="#856404">
              — expires in
              {' '}
              {formatCountdown(earlyBirdEligibility.remainingSeconds)}
            </Text>
          )}
        </Box>
      )}
      <Box
        round="var(--app-radius)"
        pad="1rem"
        background={page.cardBg}
        gap="0.5rem"
      >
        <Box gap="0.25rem" margin={{ bottom: '0.5rem' }}>
          <Text size="1rem" color={page.primaryText} weight={600}>
            Select Your Plan
          </Text>
          <Text size="0.85rem" color={page.secondaryText}>
            Choose your onboarding option and pricing model.
          </Text>
        </Box>
        <StyledFlexContainer
          direction={small ? 'column' : 'row'}
          gap="1rem"
          width="100%"
          wrap
        >
          {renderUploaderOptions()}
        </StyledFlexContainer>
      </Box>
      <Box
        round="var(--app-radius)"
        pad="1rem"
        background={page.cardBg}
        gap="0.5rem"
      >
        <Box gap="0.25rem">
          <Text size="1rem" color={page.primaryText} weight={600}>
            Upload on your timeline
          </Text>
          <Text size="0.85rem" color={page.secondaryText}>
            After onboarding, assets can be imported through your dashboard whenever
            {' '}
            you&apos;re ready.
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

CidConfigurationStep.propTypes = {
  small: PropTypes.bool.isRequired,
  offerings: PropTypes.shape({
    useCid: PropTypes.bool.isRequired,
  }).isRequired,
  channels: PropTypes.arrayOf(PropTypes.string).isRequired,
  setChannels: PropTypes.func.isRequired,
  uploader: PropTypes.oneOf(['self', 'redflag']).isRequired,
  setUploader: PropTypes.func.isRequired,
  cidPlan: PropTypes.oneOf(['A', 'B', null]),
  setCidPlan: PropTypes.func.isRequired,
  channelLimit: PropTypes.number.isRequired,
  accountKind: PropTypes.oneOf(['creator', 'agency']).isRequired,
  csvChannels: PropTypes.arrayOf(PropTypes.string),
  setCsvChannels: PropTypes.func.isRequired,
  csvFileName: PropTypes.string,
  setCsvFileName: PropTypes.func.isRequired,
  earlyBirdEligibility: PropTypes.shape({
    eligible: PropTypes.bool,
    expiresAt: PropTypes.string,
    remainingSeconds: PropTypes.number,
    reason: PropTypes.string,
  }),
  onRetryEligibility: PropTypes.func,
  allowlistChannels: PropTypes.arrayOf(PropTypes.string),
  setAllowlistChannels: PropTypes.func,
  youtubeEligibility: PropTypes.shape({
    status: PropTypes.oneOf(['idle', 'checking', 'eligible', 'ineligible', 'error']),
    data: PropTypes.shape({
      accountType: PropTypes.string,
      checkApplied: PropTypes.bool,
      eligible: PropTypes.bool,
      minimumSubscribers: PropTypes.number,
      message: PropTypes.string,
      channels: PropTypes.arrayOf(
        PropTypes.shape({
          inputUrl: PropTypes.string,
          resolvedChannelId: PropTypes.string,
          resolvedTitle: PropTypes.string,
          subscriberCount: PropTypes.number,
          eligible: PropTypes.bool,
          reasonCode: PropTypes.string,
          reason: PropTypes.string,
        }),
      ),
    }),
    error: PropTypes.string,
    channelSignature: PropTypes.string,
  }),
  pricingConfig: PropTypes.shape({
    rules: PropTypes.shape({
      youtubeCid: PropTypes.shape({
        self: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number,
            monthlyFeeAmount: PropTypes.number,
            revenueSharePercent: PropTypes.number,
          }),
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number,
            monthlyFeeAmount: PropTypes.number,
            initialMonths: PropTypes.number,
            initialSharePercent: PropTypes.number,
            afterSharePercent: PropTypes.number,
          }),
        }),
        redflag: PropTypes.shape({
          revenueShare: PropTypes.shape({
            setupFeeAmount: PropTypes.number,
            monthlyFeeAmount: PropTypes.number,
            revenueSharePercent: PropTypes.number,
          }),
          hybrid: PropTypes.shape({
            setupFeeAmount: PropTypes.number,
            monthlyFeeAmount: PropTypes.number,
            initialMonths: PropTypes.number,
            initialSharePercent: PropTypes.number,
            afterSharePercent: PropTypes.number,
          }),
        }),
      }),
    }),
    display: PropTypes.shape({
      planLabels: PropTypes.shape({
        revenueShare: PropTypes.string,
        hybrid: PropTypes.string,
      }),
      uploaderLabels: PropTypes.shape({
        self: PropTypes.string,
        redflag: PropTypes.string,
      }),
    }),
  }),
};

export default CidConfigurationStep;
